import { CommonModule } from '@angular/common';
import { Component, inject, model, OnInit, signal, viewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DxButtonModule,
  DxFormModule,
  DxScrollViewModule,
  DxToolbarModule,
  DxValidationGroupComponent,
  DxValidationGroupModule
} from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import { FormCheckboxComponent, FormTextboxComponent } from '../components';
import { CardComponent } from '../components/library/card/card.component';
import { User } from '../models/user';
import { AppInfoService } from '../services';
import { ChangePasswordComponent } from '../components/library/change-password.component';
import { UserService } from '../services/user.service';

@Component({
    selector: 'app-user-detail',
    imports: [
        CommonModule,
        DxToolbarModule,
        DxScrollViewModule,
        DxValidationGroupModule,
        DxFormModule,
        DxButtonModule,
        CardComponent,
        FormTextboxComponent,
        FormCheckboxComponent,
        ChangePasswordComponent
    ],
    template: `<div class="view-wrapper">
      <dx-toolbar class="theme-dependent" [class.scrolled]="isContentScrolled">
        <dxi-item location="before">
          <div class="header-text">User</div>
        </dxi-item>
        <dxi-item location="after" locateInMenu="never">
          <dx-button text="Save" type="default" stylingMode="contained" (onClick)="save()"></dx-button>
        </dxi-item>
      </dx-toolbar>

      <dx-scroll-view class="view-wrapper-scroll" (onScroll)="scroll($event)">
        <dx-validation-group #validationGroup>
          @if (model()) {
          <div class="cards-container">
            <card class="profile-card full-card">
              <dx-form class="form last plain-styled-form" [formData]="model" labelMode="floating">
                <dxi-item itemType="group">
                  <dxi-item>
                    <form-textbox *dxTemplate label="Name" [(value)]="model().name" [isRequired]="true" [isEditing]="true"></form-textbox>
                  </dxi-item>
                  <dxi-item>
                    <form-textbox
                      *dxTemplate
                      label="Email"
                      [(value)]="model().email"
                      [isRequired]="true"
                      [isEditing]="true"
                      [isEmail]="true"
                    ></form-textbox>
                  </dxi-item>
                  @if (isNew()) {
                  <dxi-item>
                    <form-textbox
                      *dxTemplate
                      label="Password"
                      [(value)]="model().password"
                      [isRequired]="isNew()"
                      [isEditing]="true"
                    ></form-textbox>
                  </dxi-item>
                  }
                  <dxi-item>
                    <form-checkbox *dxTemplate text="Administrator" [(value)]="model().administrator" [isEditing]="true"></form-checkbox>
                  </dxi-item>
                </dxi-item>
              </dx-form>
            </card>
            @if (!isNew()) {
            <dx-button text="Change Password" stylingMode="contained" [icon]="'lock'" (onClick)="changePassword()"></dx-button>
            <dx-button text="Delete" stylingMode="contained" [icon]="'lock'" [type]="'danger'" (onClick)="deleteUser()"></dx-button>
            }
          </div>
          }
        </dx-validation-group>
      </dx-scroll-view>
    </div>
    @if (isChangePasswordPopupOpened()) {
    <app-change-password *ngIf="isChangePasswordPopupOpened" [(visible)]="isChangePasswordPopupOpened" [userId]="key()" />
    }`,
    styleUrls: ['../mdoc/sat-referral/sat-referral.component.scss']
})
export class UserDetailComponent implements OnInit {
  userService = inject(UserService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  appInfo = inject(AppInfoService);

  model = model<User>();
  isLoading = this.appInfo.isLoading;
  key = signal<string | null>(null);
  isNew = signal<boolean>(false);
  isChangePasswordPopupOpened = signal<boolean>(false);

  validationGroup = viewChild<DxValidationGroupComponent>('validationGroup');

  isContentScrolled = false;

  async ngOnInit() {
    this.route.params.subscribe(async (params) => {
      this.isLoading.set(true);

      const key = params['id'];
      if (key && key !== 'create') {
        this.key.set(key);
        const result = await this.userService.get(key);
        if (!result.isSuccess) {
          await alert(result.message, 'Unable to load user');
          return;
        }
        this.model.set(result.value);
      } else {
        this.isNew.set(true);
        this.model.set(new User());
      }

      this.isLoading.set(false);
    });
  }

  scroll({ reachedTop = false }) {
    this.isContentScrolled = !reachedTop;
  }

  async save() {
    const isValid = this.validationGroup().instance.validate().isValid;
    if (!isValid) {
      await alert('Please make sure all parts of the form are filled in correctly', 'Unable to save user');
      return;
    }

    this.appInfo.isLoading.set(true);
    const result = await this.submit();
    this.appInfo.isLoading.set(false);

    if (!result.isSuccess) {
      await alert(result.errors?.map((error) => error.errorMessage).join('<br>'), 'Unable to save user');
      return;
    }

    this.appInfo.notify('User Saved');
    await this.router.navigate(['/admin/users']);
  }

  async submit() {
    if (this.isNew()) return await this.userService.register(this.model());
    return await this.userService.put(this.key(), this.model());
  }

  async changePassword() {
    this.isChangePasswordPopupOpened.set(true);
  }

  async deleteUser() {
    if (await confirm('Are you sure you want to delete this user?', 'Delete User')) {
      const result = await this.userService.delete(this.key());
      if (result.isSuccess) {
        this.appInfo.notify('User Deleted');
        await this.router.navigate(['/admin/users']);
      } else {
        await alert(result.detail, 'Unable to delete user');
      }
    }
  }
}
